import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { partnerApi } from "../../../../../../../services/Api/Partner/partnerApi";
import { CreatePartnerOrganizationApi, OrganizationApi } from "../../../../../../../services/Api/types";
import { getOrganizationCategories } from "../../../../../../security/utils";
import TaxIdNoWithGusInput from "../../../../../components/Admin/TaxIdNoWithGusInput/TaxIdNoWithGusInput";
import { OrganizationSearchOption } from "../../../../../components/Loan/CreateLoanModal";

interface Props {
	partnerId: number;
	onHide: (data?: OrganizationApi) => void;
	onSelectOrganization?: (org: OrganizationSearchOption) => void;
	searchValue?: string;
}

const CreatePartnerOrganizationModal = ({ partnerId, onHide, onSelectOrganization, searchValue }: Props) => {
	const [loading, setLoading] = useState(false);
	const form = useForm<CreatePartnerOrganizationApi>({
		defaultValues: {
			category: "CLUB",
		},
	});
	const { addSuccessFlash, addFlash } = useFlash();
	const {
		control,
		formState: { errors },
		setError,
		register,
		handleSubmit,
		formState,
	} = form;
	const { t } = useTranslation("admin");
	const categoryOptions = getOrganizationCategories().map((category) => ({
		label: t(`enums.organization_category.${category}`),
		value: category,
	}));

	const onSubmit = handleSubmit(async (data) => {
		setLoading(true);
		try {
			const resp = await partnerApi.createOrganizationByPartner(partnerId, data);
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			if (onSelectOrganization)
				onSelectOrganization({
					name: resp.name,
					id: resp.id,
				});
			onHide(resp);
		} catch (e) {
			handleError.form(e, setError, addFlash);
		}
		setLoading(false);
	});

	return (
		<Modal show onHide={onHide} size="lg">
			<FormDirty formState={formState} onSubmit={onSubmit}>
				<Modal.Header closeButton>
					<Modal.Title>{t("modules.partner.field.create_organization.title")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="row">
						<div className="col-6">
							<FormInput
								name={"name"}
								errors={errors}
								register={register}
								label={t("lib:common.word.name")}
								defaultValue={searchValue}
							/>
						</div>
						<div className="col-6">
							<FormSelectGroup
								control={control}
								errors={errors}
								name="category"
								label={t("common.word.category")}
								options={categoryOptions}
								data-testid="category"
							/>
						</div>
					</div>
					<FormInput
						name={"email"}
						errors={errors}
						register={register}
						label={t("modules.partner.field.user_email.title")}
					/>
					<hr />
					<h5>{t("common.word.company_info")}</h5>
					<small className="text-dark">{t("modules.partner.field.company_info.description")}</small>
					<div className="row mt-3">
						<div className="col-6">
							<FormInput
								name={"company_name"}
								errors={errors}
								register={register}
								label={t("common.word.company_name")}
							/>
						</div>
						<div className="col-6">
							<TaxIdNoWithGusInput prefix={"address."} form={form} name={"tax_id_no"} />
						</div>
					</div>
					<div className="row">
						<div className="col-6">
							<FormInput
								name="address.country"
								errors={errors}
								label={t("lib:common.address.country")}
								register={register}
							/>
						</div>
						<div className="col-6">
							<FormInput
								name="address.zip_code"
								errors={errors}
								label={t("lib:common.address.zip_code")}
								register={register}
							/>
						</div>
					</div>
					<div className="row">
						<div className="col-6">
							<FormInput
								name="address.city"
								errors={errors}
								label={t("lib:common.address.city")}
								register={register}
							/>
						</div>
						<div className="col-6">
							<FormInput
								name="address.street"
								errors={errors}
								label={t("lib:common.address.street")}
								register={register}
							/>
						</div>
					</div>
					<div className="row">
						<div className="col-6">
							<FormInput
								name="address.build_nr"
								errors={errors}
								label={t("lib:common.address.build_nr")}
								register={register}
							/>
						</div>
						<div className="col-6">
							<FormInput
								name="address.flat_nr"
								errors={errors}
								label={t("lib:common.address.flat_nr")}
								register={register}
							/>
						</div>
					</div>
					<div className="row">
						<div className="col-6">
							<FormInput
								name={"contact.phone_number"}
								errors={errors}
								register={register}
								label={t("lib:common.word.phone_number")}
							/>
						</div>
						<div className="col-6">
							<FormInput
								name={"contact.email"}
								errors={errors}
								register={register}
								label={t("lib:common.word.email")}
								help={t("modules.partner.field.email.help_text")}
							/>
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<ButtonLoading loading={loading} type={"submit"} onClick={onSubmit}>
						{t("common.action.save", { ns: "lib" })}
					</ButtonLoading>
					<Button variant="light" type="button" onClick={() => onHide()}>
						{t("common.action.cancel", { ns: "lib" })}
					</Button>
				</Modal.Footer>
			</FormDirty>
		</Modal>
	);
};
export default CreatePartnerOrganizationModal;

// t("enums.organization_category.CLUB");
// t("enums.organization_category.RESTAURANT");
// t("enums.organization_category.CAFE");
// t("enums.organization_category.FOODTRUCK");
// t("enums.organization_category.PUB_BAR");
// t("enums.organization_category.SUSHI");
// t("enums.organization_category.SHOP");
// t("enums.organization_category.OTHERS");
